import React from 'react';
import { PortfolioMixItem } from '@/pages/ResourcesBudgetCalculator';

interface Props {
  portfolioMix: PortfolioMixItem[];
  onMixChange: (idx: number, field: keyof PortfolioMixItem, value: any) => void;
  onMixSharesChange: (newShares: number[]) => void;
  currency: 'INR' | 'USD';
}



const ACTIVITY_TYPES = [
  {
    key: 'create',
    label: 'Create & Assemble',
    icon: '🛠',
    default: 30,
    cost: 100,
    hours: 2,
    beneficiaries: 10,
  },
  {
    key: 'learn',
    label: 'Learn (Workshops)',
    icon: '🧠',
    default: 15,
    cost: 80,
    hours: 1,
    beneficiaries: 8,
  },
  {
    key: 'interact',
    label: 'Interact (Mentorship)',
    icon: '💬',
    default: 15,
    cost: 120,
    hours: 2,
    beneficiaries: 5,
  },
  {
    key: 'participate',
    label: 'Participate (Cleanup, Planting)',
    icon: '🌱',
    default: 20,
    cost: 70,
    hours: 1,
    beneficiaries: 15,
  },
  {
    key: 'skills',
    label: 'Skills-based/Pro bono',
    icon: '💻',
    default: 10,
    cost: 200,
    hours: 4,
    beneficiaries: 3,
  },
  {
    key: 'vaok',
    label: 'Acts of Kindness',
    icon: '🌍',
    default: 10,
    cost: 50,
    hours: 0.5,
    beneficiaries: 20,
  },
];

function roundToNearest(num: number, step = 1) {
  return Math.round(num / step) * step;
}

const CURRENCY_SYMBOLS = {
  INR: '₹',
  USD: '$',
};

const BudgetCalculatorPortfolioMixSection: React.FC<Props> = ({ portfolioMix, onMixChange, onMixSharesChange, currency }) => {
  // Validation for shares
  const totalShare = portfolioMix.reduce((sum, item) => sum + item.share, 0);
  const shareError = totalShare !== 100 ? 'Total must be 100%' : undefined;

  // Handle slider change with auto-balancing (parent-driven)
  const handleSliderChange = (idx: number, value: number) => {
    let newSplits = portfolioMix.map(item => item.share);
    const delta = value - newSplits[idx];
    newSplits[idx] = value;
    const otherTotal = newSplits.reduce((sum, v, i) => i !== idx ? sum + v : sum, 0);
    const remaining = 100 - value;
    if (otherTotal === 0) {
      // If all others are zero, just distribute equally
      newSplits = newSplits.map((v, i) => i === idx ? value : roundToNearest(remaining / (portfolioMix.length - 1)));
    } else {
      // Scale others proportionally
      for (let i = 0; i < newSplits.length; i++) {
        if (i !== idx) {
          newSplits[i] = roundToNearest((portfolioMix[i].share / otherTotal) * remaining);
        }
      }
      // Correction for rounding errors
      const sum = newSplits.reduce((a, b) => a + b, 0);
      if (sum !== 100) {
        // Adjust the last non-active slider
        for (let i = newSplits.length - 1; i >= 0; i--) {
          if (i !== idx) {
            newSplits[i] += 100 - sum;
            break;
          }
        }
      }
    }
    onMixSharesChange(newSplits);
  };


  return (
    <section className="mb-8 p-4 bg-gray-50 rounded-lg shadow">
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
        <span role="img" aria-label="puzzle">🧩</span> Volunteering Portfolio Mix
      </h2>
      <div className="flex flex-col gap-6">
        {portfolioMix.map((type, idx) => (
          <div key={type.label} className="bg-white rounded-lg shadow p-4 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-2 min-w-[180px]">
              <span className="text-2xl">{ACTIVITY_TYPES[idx]?.icon}</span>
              <span className="font-semibold">{type.label}</span>
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={type.share}
                onChange={e => handleSliderChange(idx, Number(e.target.value))}
                className="w-full accent-green-600"
              />
              <div className="text-xs text-gray-500">{type.share}%</div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Cost/volunteer</label>
              <div className="flex items-center gap-1">
                <span className="text-gray-400">{CURRENCY_SYMBOLS[currency]}</span>
                <input
                  type="number"
                  min={0}
                  value={type.costPerVolunteer}
                  onChange={e => onMixChange(idx, 'costPerVolunteer', Number(e.target.value))}
                  className="w-24 border border-gray-300 rounded px-2 py-1"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Hours/activity</label>
              <input
                type="number"
                min={0}
                step={0.1}
                value={type.hoursPerActivity}
                onChange={e => onMixChange(idx, 'hoursPerActivity', Number(e.target.value))}
                className="w-20 border border-gray-300 rounded px-2 py-1"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Beneficiaries/activity</label>
              <input
                type="number"
                min={0}
                value={type.beneficiariesPerActivity}
                onChange={e => onMixChange(idx, 'beneficiariesPerActivity', Number(e.target.value))}
                className="w-24 border border-gray-300 rounded px-2 py-1"
              />
            </div>
          </div>
        ))}
        <div className="text-xs text-right mt-2">
          {shareError ? <span className="text-red-500 font-semibold">{shareError}</span> : <span className="text-gray-400">Portfolio mix always totals 100%.</span>}
        </div>
      </div>
    </section>
  );
};

export default BudgetCalculatorPortfolioMixSection;
